import { auth } from '../firebase'; // Import Firebase auth instance
import { Timestamp } from 'firebase/firestore'; // Import Timestamp
import { terminology } from '../config/terminology'; // Import terminology

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";
console.log('BASE_URL:', BASE_URL);

// Define convertToTimestamp at the top level
const convertToTimestamp = (dateString) => {
    if (!dateString) return null;
    try {
        // Assume dateString is 'YYYY-MM-DD' and set time to start of day UTC
        const date = new Date(dateString + 'T00:00:00Z');
        if (isNaN(date.getTime())) return null; // Invalid date string
        return Timestamp.fromDate(date);
    } catch (e) {
        console.error("Error converting date string to Timestamp:", dateString, e);
        return null;
    }
};

/**
 * Fetches a list of job schedules from the backend.
 * @param {object} [options={}] - Optional parameters for filtering and pagination.
 * @param {string} [options.fseId] - Filter by franchisee ID.
 * @param {string} [options.clientId] - Filter by client ID.
 * @param {'recurring'|'one-off'} [options.scheduleType] - Filter by schedule type.
 * @param {number} [options.pageSize=10] - The number of schedules to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @returns {Promise<object>} - The response data containing schedules and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listJobSchedules = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  console.log('listJobSchedules: BASE_URL:', BASE_URL);

  const {
    fseId = null,
    clientId = null,
    householdId = null, 
    scheduleType = null,
    quoteStatus = null, // Added quoteStatus
    pageSize = 10,
    startAfterDocId = null,
    orderBy = 'createdAt',
    orderDirection = 'desc',
    userType = 'admin',
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/schedule/list`;

    // Build the body object, only including provided filters
    const body = {
      pageSize,
      orderBy,
      orderDirection,
      userType,
    };
    if (fseId) body.fseId = fseId;
    if (clientId) body.clientId = clientId; // Keep internal name
    if (householdId) body.householdId = householdId; // Keep internal name
    if (scheduleType) body.scheduleType = scheduleType;
    if (quoteStatus) body.quoteStatus = quoteStatus; // Added quoteStatus
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list job schedules');
    }
    // Ensure the response structure matches expectations
    return responseData.data || { schedules: [], nextPageDocId: null };

  } catch (error) {
    console.error("Error listing job schedules:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Adds a new job schedule via the backend API.
 * @param {object} scheduleData - The data for the new schedule.
 * @returns {Promise<object>} - The response data containing the newly added schedule.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addJobSchedule = async (scheduleData) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated.");
    }

    // Basic validation - scheduleType and allocationType not required for quote requests
    const isQuoteRequest = scheduleData.quoteStatus === 'pending-quote';
    
    if (!scheduleData.fseId || !scheduleData.clientId || !scheduleData.householdId || !scheduleData.services) {
        // Check if clientId is the missing field specifically
        if (!scheduleData.clientId) {
             throw new Error(`Missing required schedule fields (including ${terminology.client.singular} ID (clientId)).`); // Use terminology
        }
        console.log('scheduleData:', scheduleData);
        throw new Error("Missing required schedule fields.");
    }
    
    // Only require scheduleType and allocationType for approved jobs, not quote requests
    if (!isQuoteRequest && (!scheduleData.scheduleType || !scheduleData.allocationType)) {
        console.log('scheduleData:', scheduleData);
        throw new Error("Schedule type and allocation type are required for approved jobs.");
    }
    
    // Frequency type is optional for recurring schedules now
    // if (scheduleData.scheduleType === 'recurring' && !scheduleData.frequencyType) {
    //     throw new Error("Frequency type is required for recurring schedules.");
    // }
    
    // --- Updated Validation ---
    // Frequency value is required for recurring schedules only if frequencyType is provided and is not 'Monthly'
    if (scheduleData.scheduleType === 'recurring' && scheduleData.frequencyType && scheduleData.frequencyType !== 'Monthly' && (scheduleData.frequencyValue == null || scheduleData.frequencyValue <= 0)) {
        throw new Error("Valid frequency value is required for interval-based recurring schedules (Days/Weeks).");
    }
    // --- End Updated Validation ---

    const startDateTimestamp = convertToTimestamp(scheduleData.startDate);
    const endDateTimestamp = convertToTimestamp(scheduleData.endDate);
    const jobDateTimestamp = convertToTimestamp(scheduleData.jobDate); // For one-off

    if (scheduleData.startDate && !startDateTimestamp) {
        throw new Error("Invalid Start Date provided.");
    }
    if (scheduleData.endDate && !endDateTimestamp) {
        throw new Error("Invalid End Date provided.");
    }
     if (scheduleData.scheduleType === 'one-off' && !jobDateTimestamp) {
        throw new Error("Invalid Date provided for one-off schedule.");
    }

    const payload = {
        ...scheduleData,
        startDate: startDateTimestamp,
        endDate: endDateTimestamp || null, // Ensure null if conversion failed or not provided
        jobDate: jobDateTimestamp || null, // Ensure null if conversion failed or not provided
    };

    // Remove empty optional fields more carefully
    Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '') {
            // Define required fields that should never be deleted
            const requiredFields = ['fseId', 'clientId', 'householdId', 'services', 'scheduleType', 'startDate', 'allocationType', 'frequencyType']; // Keep clientId
            // Define fields that are allowed to be null/empty string initially but should be deleted if they remain so
            const optionalFieldsToDeleteIfEmpty = ['endDate', 'occurrences', 'allotedHours', 'pricePerHour', 'additionalPrice', 'invoicePricePerHour', 'jobTime', 'description', 'adminNotes', 'notesForFranchisee', 'jobDate', 'frequencyValue'];

            if (optionalFieldsToDeleteIfEmpty.includes(key)) {
                delete payload[key];
            } else if (!requiredFields.includes(key) && (payload[key] === null || payload[key] === '')) {
                // Delete other unexpected empty/null fields, except required ones
                delete payload[key];
            }
        }
    });

    // Specific cleanup based on schedule type
    if (payload.scheduleType === 'one-off') {
        delete payload.frequencyType;
        delete payload.frequencyValue;
        delete payload.endDate;
        delete payload.occurrences;
        // Keep jobDate, delete startDate if it's redundant (assuming jobDate is derived from startDate in form)
        // Let's keep startDate for consistency, backend might ignore it for one-off
    } else { // recurring
        delete payload.jobDate;
        // Ensure frequencyValue is deleted if type is Monthly
        if (payload.frequencyType === 'Monthly') {
            delete payload.frequencyValue;
        }
    }

    // Convert frequencyType to lowercase before sending
    if (payload.frequencyType) {
        payload.frequencyType = payload.frequencyType.toLowerCase();
    }

    try {
        const idToken = await user.getIdToken();
        const url = `${BASE_URL}/schedule/add`;

        // Log the data just before sending
        console.log('Data being sent to /schedule/add:', payload);

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }
        if (responseData.status !== 'success') {
            throw new Error(responseData.message || 'Failed to add job schedule');
        }

        return responseData.data;

    } catch (error) {
        console.error("Error adding job schedule:", error);
        throw error;
    }
};

/**
 * Fetches details for a specific job schedule by ID.
 * @param {string} scheduleId - The ID of the schedule to fetch.
 * @returns {Promise<object>} - The schedule data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getScheduleById = async (scheduleId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/schedule/details`;
    const payload = { scheduleId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to fetch schedule details');
    }

    return responseData.data; // Return the schedule data object

  } catch (error) {
    console.error(`Error fetching schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing job schedule via the backend API.
 * @param {string} scheduleId - The ID of the schedule to update.
 * @param {object} updatedData - An object containing *only* the fields to update.
 * @returns {Promise<object>} - The response data containing the updated schedule and regeneration details.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const editSchedule = async (scheduleId, updatedData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required for update.");
  }
  if (!updatedData || Object.keys(updatedData).length === 0) {
      console.log("editSchedule called with no changes detected.");
      return { status: 'success', message: 'No changes detected.', data: { /* potentially empty or null */ } };
  }

  // Construct payload, starting with updatedData
  const payload = { ...updatedData };

  // Ensure date fields (startDate, endDate, jobDate) are sent as strings if they exist in the payload.
  // The updatedData from EditJobSchedulePage should already provide them as 'YYYY-MM-DD' strings.
  // No further conversion to Timestamp objects for these fields in editSchedule.
  // Example: if updatedData contains jobDate: '2024-12-31', payload.jobDate will be '2024-12-31'.

  // Add scheduleId to the final payload
  payload.scheduleId = scheduleId;

  // Remove helper/search fields that might have slipped through
  delete payload.fseNameSearch;
  delete payload.servicesDirty;
  // Add any other transient fields used only in the form

  // Remove fields with empty string values, but keep null values
  // (Backend should handle null appropriately, e.g., clearing a field)
  Object.keys(payload).forEach(key => {
    if (payload[key] === '') {
        // Decide if empty string should mean 'delete' or 'set to null'
        // Setting to null is often safer for updates if backend expects it
        payload[key] = null;
    }
  });

  // Convert frequencyType to lowercase if present in the payload
  if ('frequencyType' in payload && payload.frequencyType) {
    payload.frequencyType = payload.frequencyType.toLowerCase();
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/schedule/edit`;

    console.log('Data being sent to /schedule/edit:', payload); // Log the minimal payload

    const response = await fetch(url, {
      method: 'POST', // Assuming POST, adjust if your backend uses PUT/PATCH
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error("Server error response:", responseData);
      throw new Error(responseData.message || `Server responded with status ${response.status}`);
    }

    if (responseData.status !== 'success') {
      console.error("API error response:", responseData);
      // Include regeneration details in error if available
      const regenMessage = responseData.regenerationDetails?.generationError
          ? ` Job Regeneration Error: ${responseData.regenerationDetails.generationError}`
          : '';
      throw new Error(`${responseData.message || 'Failed to update schedule.'}${regenMessage}`);
    }

    // Return the data part which might include regeneration details
    // Ensure structure matches what the calling component expects
    return responseData; // Return the full response which includes status and data

  } catch (error) {
    console.error("Error editing job schedule:", error);
    throw error; // Re-throw the error for the page component to catch
  }
};

/**
 * Sends quote details to case manager for approval
 * @param {string} scheduleId - The ID of the schedule to send for approval
 * @param {string} notesForCaseManager - Optional notes to include in the email
 * @returns {Promise<object>} - The response data
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated
 */
export const sendQuoteToCaseManager = async (scheduleId, notesForCaseManager, latestHouseholdInfo = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/schedule/send-quote-to-case-manager`;
    const payload = { scheduleId };

    // Include notes if provided
    if (notesForCaseManager && notesForCaseManager.trim()) {
      payload.notesForCaseManager = notesForCaseManager.trim();
    }

    // Include latest household info if available
    if (latestHouseholdInfo) {
      payload.latestHouseholdInfo = latestHouseholdInfo;
    }

    console.log('Sending quote to case manager for schedule:', scheduleId, 
                'with notes:', notesForCaseManager ? 'Yes' : 'No',
                'with latest household info:', latestHouseholdInfo ? 'Yes' : 'No');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to send quote to case manager');
    }

    return responseData;

  } catch (error) {
    console.error("Error sending quote to case manager:", error);
    throw error;
  }
};

/**
 * Deletes a job schedule (soft delete).
 * Also soft-deletes all associated non-completed jobs.
 * @param {string} scheduleId - The ID of the schedule to delete.
 * @returns {Promise<object>} - Response containing deleted jobs details.
 * @throws {Error} - Throws an error if deletion fails or schedule has completed jobs.
 */
export const deleteSchedule = async (scheduleId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/schedule/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scheduleId }),
    });

    const data = await response.json();

    if (!response.ok || data.status !== 'success') {
      throw new Error(data.message || 'Failed to delete schedule');
    }

    return data;
  } catch (error) {
    console.error("Error deleting schedule:", error);
    throw error;
  }
};
